import { Injectable } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { Observable } from 'rxjs'
import { TaskRequest } from '@tv3/interfaces/requests/task-request'
import { map, switchMap, tap } from 'rxjs/operators'
import { epochToView, epochToViewUTC, Toaster } from '@tokeet/tv3-platform'
import { Task, TaskResponse } from '@tv3/store/task/task.model'
import * as R from 'ramda'
import * as moment from 'moment'

@Injectable({
  providedIn: 'root',
})
export class TaskService {
  constructor(private http: HttpClient, private toast: Toaster) {}

  all(): Observable<Task[]> {
    const url = '@api/task/all/?sort=created'

    return this.http.get<TaskResponse[]>(url).pipe(map((tasks) => this.mapTasks(tasks)))
  }

  getTask(ids: string[]) {
    const url = `@api/task/retrieve/`

    return this.http.post<TaskResponse[]>(url, { pkeys: ids }).pipe(map((tasks) => R.head(tasks)))
  }

  add(request: TaskRequest, noMessage = false, message?: string): Observable<Task> {
    const url = '@api/task/'

    return this.http.post<TaskResponse>(url, request).pipe(
      switchMap((response) => this.getTask([response.pkey])),
      map((task) => this.buildTask(task)),
      tap(() => !noMessage && this.toast.success(message ? message : 'Task created successfully.'))
    )
  }

  delete(taskId: string): Observable<Task> {
    const url = `@api/task/delete/${taskId}`

    return this.http.delete<TaskResponse>(url).pipe(
      map((task) => this.buildTask(task)),
      tap(() => this.toast.success('Task deleted successfully.'))
    )
  }

  update(payload: { request: TaskRequest; task: Task }): Observable<Task> {
    const url = `@api/task/${payload.task.id}`

    return this.http.put<TaskResponse>(url, payload.request).pipe(
      switchMap((response) => this.getTask([response.pkey])),
      map((task) => this.buildTask(task)),
      tap(() => this.toast.success('Task updated successfully.'))
    )
  }

  changeStatus(payload: { status: number; task: Task }): Observable<Task> {
    const url = `@api/task/${payload.task.id}`

    return this.http.put<TaskResponse>(url, { status: payload.status }).pipe(
      switchMap((response) => this.getTask([response.pkey])),
      map((task) => this.buildTask(task)),
      tap(() =>
        payload.status === 2
          ? this.toast.success('Task completed successfully.')
          : this.toast.success('Task updated successfully.')
      )
    )
  }

  private mapTasks(tasks: TaskResponse[]): Task[] {
    return R.map((t: TaskResponse) => this.buildTask(t), tasks)
  }

  private buildTask(t: TaskResponse): Task {
    const now = moment().unix()
    const item = {
      id: t.pkey,
      userId: t.user_id,
      inquiryId: t.inquiry_id,
      status: parseInt(<any>t.status, 10),
      notes: t.notes,
      account: t.account,
      list: t.list,
      name: t.name,
      archived: t.archived,
      created: t.created,
      due: t.due,
      dueView: epochToViewUTC(t.due),
      dueDateTimeView: epochToViewUTC(t.due, 'DD - MMM - YYYY h:mm A'),
      createdView: epochToView(t.created),
      past: now > t.due,
      pending: !(now > t.due),
    } as Task

    item.statusView = item.status === 2 ? 'Completed' : item.past ? 'Past Due' : 'Pending'
    return item
  }
}
